import { ChevronRight, Close, Layers, MoreVert, SpaceDashboard } from "@mui/icons-material";
import { Box, css, Divider, Grow, IconButton, Popper, styled, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { PopperTransitionProps } from "@mui/material/Popper/BasePopper.types";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { HudPanel } from "components/hudPanel";
import IconButtonExt from "components/iconButtonExt";
import { TooltipHotkey } from "components/tooltipHotkey";
import { CameraSpeed } from "features/cameraSpeed";
import { ClearSelection } from "features/clearSelection";
import { ClearView } from "features/clearView";
import { FlyToSelected } from "features/flyToSelected";
import { HideSelected } from "features/hideSelected";
import { Home } from "features/home";
import { MultipleSelection } from "features/multipleSelection";
import { OrthoShortcut } from "features/orthoShortcut";
import {
    CameraType,
    renderActions,
    selectCameraDefaults,
    selectCameraType,
    selectSelectMultiple,
} from "features/render";
import { SelectionColor } from "features/selectionColor";
import { ShareLink } from "features/shareLink";
import { StepBack } from "features/stepBack";
import { StepForwards } from "features/stepForwards";
import { ViewOnlySelected } from "features/viewOnlySelected";
import { useToggle } from "hooks/useToggle";
import CursorIcon from "media/icons/cursor.svg?react";
import FlyIcon from "media/icons/fly.svg?react";
import OrbitIcon from "media/icons/orbit.svg?react";
import { selectWidgetGroupPanelState } from "slices/explorer";

export function PrimaryMenuNew() {
    const theme = useTheme();
    const { t } = useTranslation();
    const [open, toggle] = useToggle(true);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const widgetGroupPanelState = useAppSelector(selectWidgetGroupPanelState);
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
    const collapsedWidth = 56;
    const expandedWidthRef = useRef<number>();
    const [width, setWidth] = useState<number | undefined>();

    const handleToggle = () => {
        setWidth(open ? collapsedWidth : expandedWidthRef.current);

        toggle();
    };

    useEffect(() => {
        if (open && panelRef.current && !expandedWidthRef.current && !isSmall) {
            expandedWidthRef.current = panelRef.current.querySelector("div")!.clientWidth;
            setWidth(expandedWidthRef.current);
        }
    }, [open, isSmall]);

    const widgetGroupPanelWidth =
        widgetGroupPanelState.expanded && widgetGroupPanelState.open
            ? theme.customSpacing.widgetGroupPanelExpandedWidth
            : widgetGroupPanelState.open
              ? 12
              : 0;

    if (isSmall) {
        return (
            <Box
                sx={{
                    zIndex: 1050,
                    position: "absolute",
                    pointerEvents: "none",
                    display: "grid",
                    placeItems: "end center",
                    bottom: 0,
                    transition: "left 0.2s",
                    left: 0,
                    width: "100%",
                }}
                ref={panelRef}
            >
                <HudPanel
                    sx={{
                        display: "flex",
                        overflow: "hidden",
                        width: "100%",
                        pointerEvents: "auto",
                        borderRadius: 0,
                        justifyContent: "center",
                        py: 1.5,
                    }}
                >
                    <Home newDesign />
                    <FlyToSelected newDesign />
                    <ViewOnlySelected newDesign />
                    <HideSelected newDesign />
                    <ClearView newDesign />
                    <MoreSelectionBtns
                        features={{
                            multipleSelection: true,
                            selectionColor: true,
                            stepForwards: true,
                            stepBack: true,
                            cameraSpeed: true,
                            shareLink: true,
                            clearSelection: true,
                            navigation: true,
                            topDown: true,
                        }}
                    />
                </HudPanel>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                zIndex: 1050,
                position: "absolute",
                pointerEvents: "none",
                display: "grid",
                placeItems: "end center",
                bottom: 0,
                left: `min(50%, 100% - ${theme.spacing(widgetGroupPanelWidth)} - ${
                    open ? (expandedWidthRef.current ?? 0) / 2 : collapsedWidth / 2
                }px)`,
                translate: "-50% 0",
                transition: "left 0.2s",
            }}
            ref={panelRef}
        >
            <HudPanel
                sx={{
                    display: "flex",
                    width: open ? width : collapsedWidth,
                    overflow: "hidden",
                    transition: "all 0.2s",
                    pointerEvents: "auto",
                }}
            >
                <Tooltip title={open ? t("collapse") : t("expand")} placement="top">
                    <Box>
                        <IconButton onClick={handleToggle}>{open ? <ChevronRight /> : <SpaceDashboard />}</IconButton>
                    </Box>
                </Tooltip>
                <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                <Home newDesign />
                <CameraSpeed newDesign />
                <StepBack newDesign />
                <CameraControlBtns />
                <FlyToSelected newDesign />
                <OrthoShortcut newDesign />
                <ShareLink variant="primaryMenu" />
                <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                <ViewOnlySelected newDesign />
                <HideSelected newDesign />
                <ClearSelection newDesign />
                <ClearView newDesign />
                <MoreSelectionBtns
                    features={{
                        multipleSelection: true,
                        selectionColor: true,
                        stepForwards: true,
                    }}
                />
            </HudPanel>
        </Box>
    );
}

function CameraControlBtns({ placement = "top" }: { placement?: "left" | "top" }) {
    const { t } = useTranslation();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const controller = useAppSelector(selectCameraDefaults).pinhole.controller;
    const cameraType = useAppSelector(selectCameraType);
    const dispatch = useAppDispatch();

    const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
    };

    let Icon: typeof CursorIcon | undefined;
    let title: string | undefined;
    switch (controller) {
        case "cadMiddlePan":
            Icon = OrbitIcon;
            title = t("cadMiddlePan");
            break;
        case "cadRightPan":
            Icon = OrbitIcon;
            title = t("cadRightPan");
            break;
        case "hover":
            Icon = FlyIcon;
            title = t("fly");
            break;
        default:
            Icon = CursorIcon;
            title = t("default");
            break;
    }

    const setController = (newController: typeof controller) => {
        closeMenu();
        dispatch(renderActions.setCameraDefaults({ pinhole: { controller: newController } }));

        if (cameraType === CameraType.Pinhole) {
            // Trigger new controller
            dispatch(renderActions.setCamera({ type: CameraType.Pinhole }));
        }
    };

    return (
        <>
            <Tooltip title={t("activeControllerType", { title })} placement="top">
                <Box>
                    <IconButtonExt
                        onClick={(e) => (menuAnchor ? closeMenu() : openMenu(e))}
                        active={Boolean(menuAnchor)}
                    >
                        <Icon />
                    </IconButtonExt>
                </Box>
            </Tooltip>

            <Popper
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                placement={placement}
                sx={{
                    zIndex: 1060,
                    display: "flex",
                    gap: 1,
                    ...(placement === "top"
                        ? {
                              flexDirection: "column",
                              pb: 1,
                          }
                        : {
                              flexDirection: "row",
                              pr: 1,
                          }),
                }}
                transition
            >
                {({ TransitionProps }) => (
                    <>
                        <Grow {...TransitionProps} timeout={150}>
                            <Tooltip
                                title={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {t("default")}
                                        <TooltipHotkey>V</TooltipHotkey>
                                    </Box>
                                }
                                placement="right"
                            >
                                <MoreSectionBtnWrapper>
                                    <IconButtonExt
                                        onClick={() => setController("flight")}
                                        active={controller === "flight"}
                                    >
                                        <CursorIcon />
                                    </IconButtonExt>
                                </MoreSectionBtnWrapper>
                            </Tooltip>
                        </Grow>
                        <Grow {...TransitionProps} timeout={200}>
                            <Tooltip
                                title={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {t("fly")}
                                        <TooltipHotkey>F</TooltipHotkey>
                                    </Box>
                                }
                                placement="right"
                            >
                                <MoreSectionBtnWrapper>
                                    <IconButtonExt
                                        onClick={() => setController("hover")}
                                        active={controller === "hover"}
                                    >
                                        <FlyIcon />
                                    </IconButtonExt>
                                </MoreSectionBtnWrapper>
                            </Tooltip>
                        </Grow>
                        <Grow {...TransitionProps} timeout={250}>
                            <Tooltip
                                title={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {t("orbit")}
                                        <TooltipHotkey>O</TooltipHotkey>
                                    </Box>
                                }
                                placement="right"
                            >
                                <MoreSectionBtnWrapper>
                                    <IconButtonExt
                                        onClick={() => setController("cadMiddlePan")}
                                        active={controller === "cadMiddlePan"}
                                    >
                                        <OrbitIcon />
                                    </IconButtonExt>
                                </MoreSectionBtnWrapper>
                            </Tooltip>
                        </Grow>
                    </>
                )}
            </Popper>
        </>
    );
}

type MoreBtnFeature =
    | "multipleSelection"
    | "selectionColor"
    | "stepForwards"
    | "stepBack"
    | "cameraSpeed"
    | "shareLink"
    | "clearSelection"
    | "navigation"
    | "topDown";

function MoreSelectionBtns({ features }: { features: Partial<Record<MoreBtnFeature, boolean>> }) {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const theme = useTheme();
    const isMultipleSelectionActive = useAppSelector(selectSelectMultiple);

    const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
    };

    return (
        <>
            <Box sx={{ position: "relative" }}>
                <IconButtonExt
                    onClick={(e) => (menuAnchor ? closeMenu() : openMenu(e))}
                    active={Boolean(menuAnchor)}
                    sx={{ transition: "rotate 0.1s", rotate: menuAnchor ? "90deg" : "0deg" }}
                >
                    {menuAnchor ? <Close /> : <MoreVert />}
                </IconButtonExt>
                <Box
                    sx={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        display: "flex",
                        flexDirection: "column",
                        transition: "opacity 0.2s",
                        opacity: menuAnchor ? 0 : 1,
                    }}
                >
                    {isMultipleSelectionActive && (
                        <Layers
                            sx={{
                                color: theme.palette.primary.main,
                                fontSize: 14,
                            }}
                        />
                    )}
                </Box>
            </Box>

            <Popper
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                placement="top"
                sx={{
                    pb: 1,
                    zIndex: 1060,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
                transition
            >
                {({ TransitionProps }) => {
                    return (
                        <>
                            {features.selectionColor && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <SelectionColor newDesign />
                                </MorePopperItem>
                            )}
                            {features.multipleSelection && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <MultipleSelection newDesign />
                                </MorePopperItem>
                            )}
                            {features.clearSelection && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <ClearSelection newDesign />
                                </MorePopperItem>
                            )}
                            {features.shareLink && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <ShareLink variant="primaryMenu" />
                                </MorePopperItem>
                            )}
                            {features.topDown && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <OrthoShortcut newDesign />
                                </MorePopperItem>
                            )}
                            {features.navigation && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <CameraControlBtns placement="left" />
                                </MorePopperItem>
                            )}
                            {features.stepForwards && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <StepForwards newDesign />
                                </MorePopperItem>
                            )}
                            {features.stepBack && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <StepBack newDesign />
                                </MorePopperItem>
                            )}
                            {features.cameraSpeed && (
                                <MorePopperItem transitionProps={TransitionProps}>
                                    <CameraSpeed newDesign />
                                </MorePopperItem>
                            )}
                        </>
                    );
                }}
            </Popper>
        </>
    );
}

function MorePopperItem({
    children,
    transitionProps,
}: {
    children: React.ReactNode;
    transitionProps: PopperTransitionProps | undefined;
}) {
    return (
        <Grow {...transitionProps} timeout={150}>
            <MoreSectionBtnWrapper>{children}</MoreSectionBtnWrapper>
        </Grow>
    );
}

const MoreSectionBtnWrapper = styled(HudPanel)(
    () => css`
        border-radius: 50%;
        padding: 2px;
    `,
);
